import React, { useEffect, useMemo, useRef, useState } from 'react';
import Modal from './ui/Modal';
import Button from './ui/Button';
import Input from './ui/Input';
import { parseScheduleTransferPayload } from '../utils/scheduleTransferFormat';

const formatPeriodLabel = (period) => {
    if (!period) return '—';
    const startWeek = period.start_week ?? period.startWeek;
    const endWeek = period.end_week ?? period.endWeek;
    if (startWeek != null && endWeek != null) {
        return `${period.name || 'Period'} (W${startWeek}–${endWeek})`;
    }
    return period.name || 'Period';
};

const ImportScheduleModal = ({
    isOpen,
    onClose,
    onImport,
    timePeriods = [],
    selectedPeriodId = null,
    isImporting = false
}) => {
    const fileInputRef = useRef(null);
    const [periodId, setPeriodId] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [file, setFile] = useState(null);
    const [parsedEntries, setParsedEntries] = useState([]);
    const [fileError, setFileError] = useState(null);

    useEffect(() => {
        if (!isOpen) return;

        const defaultPeriodId = selectedPeriodId != null && selectedPeriodId !== ''
            ? String(selectedPeriodId)
            : (timePeriods[0]?.id != null ? String(timePeriods[0].id) : '');
        const defaultPeriod = timePeriods.find((period) => String(period.id) === String(defaultPeriodId));

        setPeriodId(defaultPeriodId);
        setDisplayName(defaultPeriod?.name ? `${defaultPeriod.name} import` : 'Imported schedule');
        setFile(null);
        setParsedEntries([]);
        setFileError(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, [isOpen, selectedPeriodId, timePeriods]);

    const selectedPeriod = useMemo(
        () => timePeriods.find((period) => String(period.id) === String(periodId)) || null,
        [periodId, timePeriods]
    );

    const handleFileChange = async (event) => {
        const nextFile = event.target.files?.[0];
        if (!nextFile) {
            setFile(null);
            setParsedEntries([]);
            setFileError(null);
            return;
        }

        const extension = nextFile.name.split('.').pop()?.toLowerCase();
        setFile(nextFile);
        setFileError(null);

        if (extension === 'csv') {
            setParsedEntries([]);
            if (!displayName.trim()) {
                setDisplayName(nextFile.name.replace(/\.[^/.]+$/, ''));
            }
            return;
        }

        if (extension !== 'json') {
            setParsedEntries([]);
            setFileError('Please choose a .json or .csv file.');
            return;
        }

        try {
            const text = await nextFile.text();
            const parsed = parseScheduleTransferPayload(JSON.parse(text));
            setParsedEntries(parsed.entries);
            if (!displayName.trim() || displayName.endsWith(' import')) {
                const fromFile = nextFile.name.replace(/\.[^/.]+$/, '');
                setDisplayName(fromFile || displayName);
            }
        } catch (error) {
            setParsedEntries([]);
            setFileError(error.message || 'Could not read that JSON file.');
        }
    };

    const canSubmit = Boolean(
        periodId
        && displayName.trim()
        && file
        && !fileError
        && !isImporting
        && (file.name.toLowerCase().endsWith('.csv') || parsedEntries.length > 0)
    );

    const handleSubmit = (event) => {
        event.preventDefault();
        if (!canSubmit) return;

        onImport({
            file,
            period: selectedPeriod,
            periodId,
            displayName: displayName.trim(),
            entries: parsedEntries
        });
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={isImporting ? undefined : onClose}
            title="Import schedule"
            containerClassName="max-w-lg max-h-[80vh]"
        >
            <form className="space-y-4" onSubmit={handleSubmit}>
                <p className="text-sm text-gray-600">
                    Import creates a new version in the period you choose. The live schedule stays unchanged until you make that version live.
                </p>

                <div>
                    <label htmlFor="import-period" className="block text-sm font-medium text-gray-700 mb-1">
                        Period <span className="text-red-500">*</span>
                    </label>
                    <select
                        id="import-period"
                        value={periodId}
                        onChange={(event) => {
                            const nextId = event.target.value;
                            setPeriodId(nextId);
                            const period = timePeriods.find((item) => String(item.id) === String(nextId));
                            if (period?.name && (!displayName.trim() || displayName.endsWith(' import'))) {
                                setDisplayName(`${period.name} import`);
                            }
                        }}
                        disabled={isImporting}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
                    >
                        <option value="" disabled>Select a period</option>
                        {timePeriods.map((period) => (
                            <option key={period.id} value={String(period.id)}>
                                {formatPeriodLabel(period)}
                            </option>
                        ))}
                    </select>
                </div>

                <Input
                    id="import-version-name"
                    name="displayName"
                    label="New version name"
                    required
                    value={displayName}
                    onChange={(event) => setDisplayName(event.target.value)}
                    disabled={isImporting}
                    placeholder="Fall import"
                />

                <div>
                    <label htmlFor="import-file" className="block text-sm font-medium text-gray-700 mb-1">
                        Schedule file <span className="text-red-500">*</span>
                    </label>
                    <input
                        ref={fileInputRef}
                        id="import-file"
                        type="file"
                        accept=".json,.csv"
                        onChange={handleFileChange}
                        disabled={isImporting}
                        className="w-full text-sm text-gray-700 file:mr-3 file:rounded-md file:border-0 file:bg-gray-100 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-gray-700 hover:file:bg-gray-200"
                    />
                    {file && (
                        <p className="mt-1 text-xs text-gray-500">
                            {file.name}
                            {parsedEntries.length > 0 ? ` · ${parsedEntries.length} entries` : ''}
                        </p>
                    )}
                    {fileError && (
                        <p className="mt-1 text-sm text-red-600">{fileError}</p>
                    )}
                </div>

                <div className="flex justify-end gap-2 pt-2">
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={onClose}
                        disabled={isImporting}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="primary"
                        disabled={!canSubmit}
                    >
                        {isImporting ? 'Importing…' : 'Import into new version'}
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

export default ImportScheduleModal;
